/**
 * The site's pages, as data.
 *
 * One page was one query. These are the questions people actually type — "how do I make an .ics
 * file", "add to Google Calendar link", "how do I make an event repeat" — and each gets its own
 * URL, its own title, and copy that answers it, above the same creator. The list is the single
 * source of truth: the router picks from it, the prerender writes one HTML file per entry, the
 * sitemap lists them, and the footer links them together.
 *
 * Every page must earn its URL. Near-duplicate pages built from a keyword list are worse than
 * no pages at all — search engines collapse them and the site looks like a doorway farm — so
 * each answer here is specific to this tool and true of it.
 *
 * There is a second reader now. An assistant asked "how do I make an .ics file" does not rank
 * pages, it lifts a passage and repeats it, usually without the paragraph before or after. So
 * each page carries a `question`/`answer` pair: the query in the words someone would use, and
 * a reply that stands on its own — it names the tool rather than saying "this page", and it
 * states numbers and limits rather than adjectives, because a quotable sentence is a concrete
 * one. That passage is rendered at the top of the page, emitted as the page's JSON-LD
 * description, and written into /llms.txt, all from this one string, so the three cannot drift
 * apart and leave an engine to decide which of our own descriptions of ourselves to believe.
 */

// Aliased because this file exports its own `LIMITS` — the things the tool cannot do, which is a
// different list from the field ceilings the sanitizer enforces. Both are read here: a page that
// tells people "any number of guests" while the sanitizer quietly drops the 101st is exactly the
// kind of claim an answer engine repeats and a reader then discovers is false.
import { CSV_HEADER, LIMITS as FIELD_LIMITS } from '@project/event-format';

export interface FaqItem {
  readonly question: string;
  /** Plain text, not markup: it is rendered on the page *and* emitted as FAQPage JSON-LD. */
  readonly answer: string;
}

export interface Page {
  /** '' is the home page; everything else serves from `/<slug>/`. */
  readonly slug: string;
  /** `<title>` — under ~60 characters, since Google truncates past that. */
  readonly title: string;
  /** `<meta name="description">` — under ~160 characters, for the same reason. */
  readonly description: string;
  /** Footer link text. */
  readonly navLabel: string;
  readonly h1: string;
  readonly lede: string;
  /** The query this page exists to answer, in the words a person would actually use. */
  readonly question: string;
  /**
   * The answer to `question`, in one passage that survives being quoted alone: it names ICS
   * Maker instead of "this page", and it prefers a number or a limit to an adjective. Plain
   * text, not markup — it is rendered, put in JSON-LD, and written to /llms.txt.
   */
  readonly answer: string;
  /** The prose below the creator. */
  readonly body: () => React.JSX.Element;
  readonly faq: readonly FaqItem[];
}

/** A complete translation of one page. HTML comes only from checked-in build-time catalogues. */
export interface PageTranslation {
  readonly title: string;
  readonly description: string;
  readonly navLabel: string;
  readonly h1: string;
  readonly lede: string;
  readonly question: string;
  readonly answer: string;
  readonly bodyHtml: string;
  readonly faq: readonly FaqItem[];
}

export const pageTranslationKey = (page: Page): string => page.slug || 'home';

function safeBodyHtml(html: string): string {
  const withoutKnownTags = html.replace(/<\/?(?:h2|p|ol|li|dl|div|dt|dd|pre|code|em)>/g, '');
  if (withoutKnownTags.includes('<') || withoutKnownTags.includes('>')) {
    throw new Error('Localized page body contains markup outside the allowed content tags.');
  }
  return html;
}

export function localizePage(page: Page, translation: PageTranslation | undefined): Page {
  if (translation === undefined) return page;
  const bodyHtml = safeBodyHtml(translation.bodyHtml);
  return {
    ...page,
    ...translation,
    body: () => <div className="localized-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />,
  };
}

const FORMATS = [
  [
    '.ics (iCalendar)',
    'Apple Calendar, Google Calendar, Outlook, Thunderbird — the universal file.',
  ],
  [
    'jCal',
    'The same event as JSON (RFC 7265) — for anything that would rather parse JSON than iCalendar.',
  ],
  ['CSV', "Google Calendar and Outlook's spreadsheet importer, for bulk or awkward cases."],
  [
    'JSON-LD',
    'schema.org/Event markup, so search engines and browsers can read the event off a page.',
  ],
] as const;

const EMBED_SNIPPET = `<div id="event-creator"></div>
<script src="https://icsmaker.com/event-creator.js"></script>
<script>
  EventCreator.mount('#event-creator', { showPreview: false });
</script>`;

/** Exactly what `alarmLines` writes, so the page cannot describe an alarm the file does not have. */
const ALARM_SNIPPET = `BEGIN:VALARM
ACTION:DISPLAY
DESCRIPTION:Reminder
TRIGGER:-PT30M
END:VALARM`;

/** The CSV header row, verbatim — the whole reason someone lands on that page. */
const CSV_HEADER_LINE = CSV_HEADER.join(',');

const HOME: Page = {
  slug: '',
  title: 'Free ICS Maker — Create iCalendar Files Online',
  description:
    'Create and download .ics calendar files for Google Calendar, Outlook and Apple Calendar. Build an event or turn a schedule into events. Free, no signup or uploads.',
  navLabel: 'Make an event',
  h1: 'Create an event for your calendar',
  lede: 'Fill in the details and download a calendar file (.ics) for Google Calendar, Outlook or Apple Calendar. Free, no account needed.',
  question: 'How do I create a calendar event file (.ics) without an account?',
  answer:
    'ICS Maker is a free online iCalendar (.ics) file generator. Enter an event title, start and end, then download the file and import it into Google Calendar, Outlook, or Apple Calendar. You can also paste a schedule or attach a document or image, review the detected events, and download several events together in one .ics file. The event form supports time zones, recurrence, guests and reminders, plus jCal, CSV, JSON-LD and calendar links. No account is required; text recognition, document reading and file generation run in your browser without uploading your event data.',
  body: () => (
    <>
      <h2>How to create an ICS file</h2>
      <ol>
        <li>Enter the event title, start and end in the form.</li>
        <li>
          Choose a time zone for an event tied to a place, or leave it unset for local clock time.
        </li>
        <li>Add any repeat rule, reminders, location or notes, then download the .ics file.</li>
        <li>
          Open the file in your calendar app, or use Google Calendar’s Import &amp; export settings.
        </li>
      </ol>
      <h2>Turn a schedule, document or photo into calendar events</h2>
      <p>
        Use the other input tab to paste a schedule or attach a document, spreadsheet or flyer. ICS
        Maker reads the text locally and collects the events it recognises. Check each title, date
        and time, correct any flagged fields, then download the collected events as one .ics file.
        You can also add events to the list from the form.
      </p>
      <p>
        Regular schedules work better than free-form prose. Image recognition can misread text;
        ambiguous dates need review. For a scanned PDF, attach a photo of a page or paste its text.
        Importing a downloaded file copies the events; it does not create a live subscription.
      </p>
      <h2>Formats it generates</h2>
      <dl>
        {FORMATS.map(([name, description]) => (
          <div key={name}>
            <dt>{name}</dt>
            <dd>{description}</dd>
          </div>
        ))}
      </dl>

      <h2>Why it asks so little</h2>
      <p>
        There is no account, because an event you build in your browser needs nobody&rsquo;s server.
        The page loads once and then talks to nothing: the file is assembled from what you typed and
        handed to your own download folder. Unfinished work is kept in this browser tab’s session
        storage so refreshing the page does not erase it. Nothing is uploaded.
      </p>
    </>
  ),
  faq: [
    {
      question: 'What is an .ics file?',
      answer:
        'An .ics file is a plain-text calendar event in the iCalendar format defined by RFC 5545. Every mainstream calendar — Apple Calendar, Google Calendar, Outlook, Thunderbird — reads it, which is why an invitation emailed between two different companies still lands correctly in both calendars.',
    },
    {
      question: 'Do I need an account?',
      answer:
        'No. The page has no signup, no login, and no server component. The event is assembled in your browser and downloaded from there, so nothing you type is uploaded.',
    },
    {
      question: 'Is it free?',
      answer:
        'Yes, and there is no paid tier holding features back. The same builder is also available as an embeddable widget for your own site.',
    },
    {
      question: 'Will the event keep its time zone?',
      answer:
        'Only if you choose one. By default the event carries no time zone, so it opens at the clock time you typed wherever it is added — usually what you want for a local appointment. Pick a zone from the list when the event is tied to one place, and every guest sees it converted to their own.',
    },
    {
      question: 'Can an event end in a different time zone from the one it starts in?',
      answer:
        'Yes. Pick a start zone, then tick "Ends in a different time zone" and choose the second one. The file carries a zone on the start and another on the end, which is exactly how a flight is meant to be written — it leaves at 15:00 in London and lands at 11:00 in New York.',
    },
  ],
};

const ICS_FILE: Page = {
  slug: 'ics-file',
  title: 'What is an .ics file, and how do you make one?',
  description:
    'An .ics file is a calendar event in the iCalendar format (RFC 5545). Here is what is inside one, how to open it on any device, and a builder that makes one.',
  navLabel: 'What is an .ics file',
  h1: 'The .ics file, explained',
  lede: 'An .ics file is one calendar event — or several — written as plain text in a format every calendar app agrees on. Build one below, or read what is actually inside it.',
  question: 'What is an .ics file?',
  answer:
    'An .ics file is one calendar event — or several — written as plain text in the iCalendar format, standardised as RFC 5545 in 2009 and unchanged since. The event sits between BEGIN:VEVENT and END:VEVENT, where SUMMARY is the title, DTSTART and DTEND are the times, LOCATION and DESCRIPTION are what they sound like, and UID is the identifier a calendar uses to tell an update from a new event. Apple Calendar, Google Calendar, Outlook, and Thunderbird all read it, which is why an invitation emailed between two companies lands correctly in both. ICS Maker builds one from a form and shows the exact file text beside it as you type.',
  body: () => (
    <>
      <h2>What is inside one</h2>
      <p>
        Open an .ics file in a text editor and it reads as a short list of properties. The event
        itself sits between <code>BEGIN:VEVENT</code> and <code>END:VEVENT</code>:{' '}
        <code>SUMMARY</code> is the title, <code>DTSTART</code> and <code>DTEND</code> are the
        times, <code>LOCATION</code> and <code>DESCRIPTION</code> are what you would expect, and{' '}
        <code>UID</code> is the identifier a calendar uses to tell an update from a new event. The
        format is RFC&nbsp;5545, published in 2009 and unchanged since — which is why a file written
        today opens in software written a decade ago.
      </p>
      <p>
        The preview beside the form is the real file, updating as you type. Nothing is hidden from
        you and nothing is added: what you read there is byte for byte what downloads.
      </p>

      <h2>How to open one</h2>
      <dl>
        <div>
          <dt>macOS and iOS</dt>
          <dd>Double-click or tap the file; Calendar offers to add the event.</dd>
        </div>
        <div>
          <dt>Google Calendar</dt>
          <dd>
            Settings → Import &amp; export → Import, then pick the file and the calendar it should
            land in.
          </dd>
        </div>
        <div>
          <dt>Outlook</dt>
          <dd>
            File → Open &amp; Export → Import/Export → Import an iCalendar (.ics) file, or just
            double-click it in Windows.
          </dd>
        </div>
        <div>
          <dt>Android</dt>
          <dd>
            Most Android calendars import through Google Calendar on the web rather than from the
            phone — mail yourself the file and open it on a desktop.
          </dd>
        </div>
      </dl>
    </>
  ),
  faq: [
    {
      question: 'What does ICS stand for?',
      answer:
        'It is simply the file extension for iCalendar data — short for "iCalendar Stream". The format itself is defined by RFC 5545, and the same content is sometimes served with the media type text/calendar.',
    },
    {
      question: 'Is .ics the same as iCal?',
      answer:
        'Nearly. iCal was Apple’s calendar app, now just called Calendar, and people borrowed the name for the format it read. The format’s real name is iCalendar and its files end in .ics.',
    },
    {
      question: 'Can one .ics file hold several events?',
      answer:
        'Yes. An .ics file can contain multiple VEVENT blocks. In ICS Maker, collect events from the form, a pasted schedule or an attached file. Review and correct them, then download them together as one .ics file. Importing copies the events; it does not create a calendar subscription.',
    },
    {
      question: 'Can I edit an .ics file by hand?',
      answer:
        'You can, and it is a reasonable way to fix a typo — it is plain text with one property per line. Keep the line endings as CRLF and keep long lines folded, or strict parsers will complain.',
    },
  ],
};

const GOOGLE: Page = {
  slug: 'add-to-google-calendar',
  title: 'Add to Google Calendar link generator',
  description:
    'Build an event and get a Google Calendar link that opens it prefilled — plus Outlook, Office 365, and Yahoo links, and the .ics file for everything else.',
  navLabel: 'Add to Google Calendar',
  h1: 'Add-to-Google-Calendar links',
  lede: 'Fill in the event and take the Google Calendar link beside it. Anyone who clicks it lands in Google Calendar with the event already filled in, one click from saving it.',
  question: "How do I make an 'add to Google Calendar' link?",
  answer:
    "A Google Calendar link is an ordinary URL — calendar.google.com/calendar/render — carrying the title, times, location, and description as query parameters. Clicking it opens Google Calendar with the event already filled in, and nothing is stored on Google's side until the person presses save. ICS Maker builds that link from a form and produces the Outlook.com, Office 365, and Yahoo equivalents from the same event, plus the .ics file for the calendars that are none of the four. Nobody needs an ICS Maker account, because there are none; the recipient needs a Google account only to save the event into it.",
  body: () => (
    <>
      <h2>What the link actually is</h2>
      <p>
        Google accepts an event as a URL: <code>calendar.google.com/calendar/render</code> with the
        title, times, location, and description as query parameters. Nothing is stored on
        Google&rsquo;s side until the person clicks save, so the link works for anyone — they do not
        need to be the account that made it, and you do not need an account at all to build one.
      </p>
      <p>
        The same event produces links for Outlook.com, Office&nbsp;365, and Yahoo at the same time,
        and downloads as an .ics file for Apple Calendar, Thunderbird, and everything else. Pick
        whichever your audience uses; the event is identical in all of them.
      </p>

      <h2>Where a link beats a file</h2>
      <p>
        A link is better in an email, a newsletter, or a web page: one tap, no download, nothing to
        open. A file is better when the recipient&rsquo;s calendar is not one of the four web
        services above, when the event must survive being forwarded, or when you want it saved
        somewhere permanent. Most invitations want both — a link for convenience and a file for the
        people whose calendar you cannot guess.
      </p>
    </>
  ),
  faq: [
    {
      question: 'Does the person clicking the link need a Google account?',
      answer:
        'They need one to save the event, since it lands in their own calendar. The link itself is public and works for anyone; Google prompts them to sign in if they are not already.',
    },
    {
      question: 'Can I put the link on a button on my site?',
      answer:
        'Yes — it is an ordinary URL, so any anchor or button works. If you would rather let visitors build their own event, the whole builder embeds with one script tag.',
    },
    {
      question: 'Will invitees I add get an invitation?',
      answer:
        'Not from the link. Invitee addresses travel in the .ics file, and it is the calendar or mail client that sends invitations when the organiser saves the event. A prefilled Google link fills the fields; it does not send mail on your behalf.',
    },
    {
      question: 'What happens to the time zone in a link?',
      answer:
        'Google is told the zone explicitly when you pick one, so everyone sees the same instant. With no zone chosen, the event uses the clock time you typed, interpreted in whatever zone the person saving it lives in.',
    },
  ],
};

/**
 * Repeat rules people actually search for, as text they can paste. The form covers frequency,
 * interval, and how the series ends; the `BYDAY`/`BYMONTHDAY`/`BYSETPOS` patterns below it
 * cannot, so the copy marks those as a hand edit rather than implying the form can do them.
 */
const RRULE_RECIPES = [
  ['Every week, same day', 'FREQ=WEEKLY'],
  ['Every other week (fortnightly)', 'FREQ=WEEKLY;INTERVAL=2'],
  ['Every weekday, Monday to Friday', 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR'],
  ['The 1st of every month', 'FREQ=MONTHLY;BYMONTHDAY=1'],
  ['The last day of every month', 'FREQ=MONTHLY;BYMONTHDAY=-1'],
  ['The first Monday of every month', 'FREQ=MONTHLY;BYDAY=1MO'],
  ['The last Friday of every month', 'FREQ=MONTHLY;BYDAY=-1FR'],
  ['The last working day of every month', 'FREQ=MONTHLY;BYDAY=MO,TU,WE,TH,FR;BYSETPOS=-1'],
  ['Every three months (quarterly)', 'FREQ=MONTHLY;INTERVAL=3'],
  ['Every year, on the start date', 'FREQ=YEARLY'],
  ['Twelve times, then stop', 'FREQ=MONTHLY;COUNT=12'],
  ['Weekly until a fixed date', 'FREQ=WEEKLY;UNTIL=20261231T235959Z'],
] as const;

const RECURRING: Page = {
  slug: 'recurring-event',
  title: 'Repeating calendar event (.ics RRULE generator + examples)',
  description:
    'Build a repeating event and download it as an .ics file with a proper RRULE — plus copy-paste rules for every other week, the last day of the month, and weekdays only.',
  navLabel: 'Repeating events',
  h1: 'Repeating events',
  lede: 'Set the event to repeat daily, weekly, monthly, or yearly. The file carries a standard repeat rule, so the calendar that opens it creates the whole series rather than a single event.',
  question: 'How do I make a calendar event repeat every week or every month?',
  answer:
    "A repeat is one line in the .ics file: RRULE:FREQ=WEEKLY, with INTERVAL for every second or third period and COUNT or UNTIL to end the series. ICS Maker's form sets the frequency (daily, weekly, monthly, yearly), the interval, and whether the series never ends, stops after a number of occurrences, or runs to a date — and writes the RRULE into the downloaded file, which the reading calendar expands itself, so a series of any length stays one small file. Choosing a particular day is not in the form and is a one-line edit to the file: FREQ=MONTHLY;BYMONTHDAY=-1 for the last day of the month, BYDAY=1MO for the first Monday, BYDAY=MO,TU,WE,TH,FR for weekdays only. This page lists twelve such rules to paste.",
  body: () => (
    <>
      <h2>The rule inside the file</h2>
      <p>
        A repeat is one line: <code>RRULE:FREQ=WEEKLY</code>. Calendars read it and generate the
        occurrences themselves, which is why a repeating event stays one small file no matter how
        long it runs. The rule can also carry an interval (<code>INTERVAL=2</code> for fortnightly),
        an end date (<code>UNTIL</code>), or a limit (<code>COUNT</code>).
      </p>
      <p>
        The builder writes all of it. Pick a frequency, set the interval — every 2 weeks, every 3
        months — and choose whether the series never ends, stops after a number of occurrences, or
        runs until a date. The line it produces is standard, so the calendar that opens the file
        creates exactly the series described.
      </p>

      <h2>Repeat rules you can paste</h2>
      <p>
        The form covers frequency, interval, and how the series ends. Picking a particular day — the
        last day of the month, the first Monday, weekdays only — is a one-line edit to the file
        after you download it: open it in a text editor and replace the <code>RRULE:</code> line
        with one of these. The parts are all from RFC&nbsp;5545, so Google Calendar, Outlook, Apple
        Calendar, and Thunderbird all read them the same way.
      </p>
      <dl>
        {RRULE_RECIPES.map(([what, rule]) => (
          <div key={rule}>
            <dt>{what}</dt>
            <dd>
              <code>RRULE:{rule}</code>
            </dd>
          </div>
        ))}
      </dl>

      <h2>How the parts fit together</h2>
      <p>
        <code>FREQ</code> is the only required part; everything else narrows it.{' '}
        <code>INTERVAL</code> skips periods, so <code>FREQ=WEEKLY;INTERVAL=2</code> is every second
        week rather than every second day. <code>BYDAY</code> takes two-letter weekday codes, and a
        number in front of one picks an occurrence within the month — <code>1MO</code> is the first
        Monday, <code>-1FR</code> the last Friday. <code>BYMONTHDAY</code> counts dates instead, and
        counts backwards from the end when negative, which is what makes <code>BYMONTHDAY=-1</code>{' '}
        the reliable way to say &ldquo;the last day&rdquo;. <code>BYSETPOS</code> picks from
        whatever the rest of the rule matched in each period, so a weekday list with{' '}
        <code>BYSETPOS=-1</code> lands on the last working day.
      </p>

      <h2>Which frequency to pick</h2>
      <p>
        Weekly is anchored to the day of the week you chose for the start, monthly to the date. A
        monthly event on the 31st is the classic trap: months without one are skipped by most
        calendars rather than moved, so use <code>BYMONTHDAY=-1</code> when you mean the last day of
        every month, rather than starting the series on the 31st and hoping.
      </p>
    </>
  ),
  faq: [
    {
      question: 'How do I stop a repeating event?',
      answer:
        'Set "Repeat ends" to a number of times or to a last date, and the file carries COUNT or UNTIL accordingly. Leave it on Never and the series runs until you end it in your own calendar, which can always edit a series it imported.',
    },
    {
      question: 'What is the difference between COUNT and UNTIL?',
      answer:
        'COUNT stops the series after a number of occurrences — COUNT=12 gives twelve and no more, whatever the dates work out to be. UNTIL stops it at a moment in time, written as a UTC timestamp like UNTIL=20261231T235959Z, and any occurrence that would fall after it simply does not happen. They do the same job from opposite ends, and RFC 5545 does not allow both in one rule.',
    },
    {
      question: 'Can I make an event repeat every other week?',
      answer:
        'Yes — choose Weekly and set the interval to 2. The file gets INTERVAL=2 on its repeat rule, which every calendar reads as fortnightly. The same works for every 3 days, every 6 months, and so on.',
    },
    {
      question: 'How do I repeat on the last day of every month?',
      answer:
        'Use FREQ=MONTHLY;BYMONTHDAY=-1, which is a hand edit to the downloaded file — the form sets the frequency, interval, and end, but not which day of the month is picked. Negative numbers count backwards from the end, so it lands on the 28th, 29th, 30th, or 31st as each month requires. Starting a monthly series on the 31st instead is the common mistake: most calendars skip the months that have no 31st rather than moving the event.',
    },
    {
      question: 'How do I skip one occurrence of a series?',
      answer:
        'Add an EXDATE line beside the RRULE, listing the start time of the occurrence to drop — EXDATE:20261225T090000Z removes that one instance and leaves the rest. To move it rather than delete it, pair the EXDATE with a second VEVENT carrying the same UID and a RECURRENCE-ID pointing at the original slot.',
    },
    {
      question: 'Will the repeat survive being imported into Outlook or Google?',
      answer:
        'Yes. RRULE is part of RFC 5545 and both read it, along with Apple Calendar and Thunderbird. They expand the series on import rather than storing every occurrence.',
    },
  ],
};

const EMBED: Page = {
  slug: 'add-to-calendar-button',
  title: 'Add-to-calendar button and event builder for your site',
  description:
    'Embed the event builder on your own page with one script tag. No framework, no tracking, no account — one self-contained file, or a React component.',
  navLabel: 'Put it on your site',
  h1: 'Put the builder on your own site',
  lede: 'The creator on this page is a component, and it ships as a standalone bundle. One script tag on your page, one element for it to mount into, and your visitors build their own events without leaving your site.',
  question: 'How do I put an add-to-calendar button or event builder on my own site?',
  answer:
    "ICS Maker's builder ships as a standalone script: one empty element, one script tag pointing at event-creator.js, and one EventCreator.mount() call — no build step, no framework, no account, no key, and no per-view limit. The bundle is a single self-contained IIFE under 85 kB gzipped with its rendering library included, and it makes no network requests at all: no analytics, no fonts, no CDN, so it adds no origin to a Content-Security-Policy and no paragraph to a privacy policy. Every colour, radius, and font is a CSS custom property under .ec-root, dark mode follows the visitor's own preference, and a React site can render the component directly instead of loading the bundle.",
  body: () => (
    <>
      <h2>One script tag</h2>
      <pre>
        <code>{EMBED_SNIPPET}</code>
      </pre>
      <p>
        The bundle carries its own rendering, its own styles, and no dependencies for you to
        install. It talks to nothing — no analytics, no fonts, no CDN — so your page&rsquo;s privacy
        policy needs no new paragraph, and a strict Content-Security-Policy needs no new origin.
      </p>

      <h2>Making it look like your site</h2>
      <p>
        Every colour, radius, and font is a CSS custom property under <code>.ec-root</code>, so
        overriding <code>--ec-accent</code> is usually the whole theming job. It follows the
        visitor&rsquo;s dark-mode preference on its own, and the preview panel can be turned off
        with <code>showPreview: false</code> where space is tight.
      </p>

      <h2>Already using React?</h2>
      <p>
        Skip the bundle and render the component directly — same form logic, one less copy of the
        rendering library on the page.
      </p>
    </>
  ),
  faq: [
    {
      question: 'Does the embed send anything to your servers?',
      answer:
        'No. It is a static file that runs entirely in the visitor’s browser: no analytics, no telemetry, no calls home. You can host the bundle yourself if you would rather not load it from this domain.',
    },
    {
      question: 'How big is it?',
      answer:
        'Under 85 kB gzipped, rendering library included, and it loads after your page rather than blocking it. The build fails if the bundle ever grows past that, so the number on this page cannot quietly stop being true.',
    },
    {
      question: 'Can I restrict which formats visitors can download?',
      answer:
        'The mount call takes options — a default format, a default date, a prefilled draft, and whether to show the preview — and the React component takes the same ones as props.',
    },
  ],
};

const TIME_ZONE: Page = {
  slug: 'ics-time-zone',
  title: 'Calendar event at the wrong time? Fixing .ics time zones',
  description:
    'An .ics event can land an hour or a day out. The three ways a file can express a time, which one you want, and how to read the one you were sent.',
  navLabel: 'Wrong time zone',
  h1: 'When the event lands at the wrong time',
  lede: 'Almost every “the invitation is an hour out” problem is one of three things: a time with no zone, a zone the reading calendar does not recognise, or an all-day event being treated as a timed one.',
  question: 'Why is my calendar event an hour, or a day, out?',
  answer:
    "An .ics file can express a time in three ways, and the wrong one is nearly always the cause. A floating time (DTSTART:20260815T090000, no zone) is nine o'clock wherever it is opened. A UTC time (a trailing Z) is one absolute instant. A named zone (DTSTART;TZID=Europe/London:...) is an instant that stays at the same local clock time across daylight saving. An hour-out import is usually a Windows-style zone name such as 'GMT Standard Time' that the reading calendar does not recognise and falls back to UTC on; a day-out all-day event is usually a midnight timestamp where VALUE=DATE was needed, or an end date written on the last day rather than the day after, since DTEND is exclusive. ICS Maker writes a floating time by default and a TZID naming an IANA zone when one is chosen — without a bundled VTIMEZONE block, which every mainstream calendar resolves from the zone name itself.",
  body: () => (
    <>
      <h2>The three ways a file can say when</h2>
      <dl>
        <div>
          <dt>Floating — no zone at all</dt>
          <dd>
            <code>DTSTART:20260815T090000</code>. Nine in the morning wherever it is opened. Right
            for a local appointment, a reminder, or anything defined by the reader&rsquo;s own
            clock; wrong the moment two people in different countries need to meet.
          </dd>
        </div>
        <div>
          <dt>UTC — an absolute instant</dt>
          <dd>
            <code>DTSTART:20260815T090000Z</code>, with the trailing <code>Z</code>. One moment in
            time, converted to local time by whatever opens it. Unambiguous, but it loses the fact
            that the meeting was &ldquo;ten o&rsquo;clock in London&rdquo;, so a daylight-saving
            change moves it.
          </dd>
        </div>
        <div>
          <dt>Named zone — an instant that survives daylight saving</dt>
          <dd>
            <code>DTSTART;TZID=Europe/London:20260815T090000</code>. What a recurring meeting wants:
            the series stays at ten o&rsquo;clock local even as the offset shifts underneath it.
            RFC&nbsp;5545 asks for a matching <code>VTIMEZONE</code> block defining that
            zone&rsquo;s offsets alongside it.
          </dd>
        </div>
      </dl>
      <p>
        This builder writes the first by default and the third when you pick a zone from the list.
        That default is deliberate: a dentist appointment at nine should be at nine, and attaching
        the zone of whatever machine happened to build the file is how an event ends up an hour out
        for the person who reads it.
      </p>
      <p>
        It writes the <code>TZID</code> without a bundled <code>VTIMEZONE</code> block, which is
        worth knowing before you send the file somewhere unusual. Apple Calendar, Google Calendar,
        Outlook, and Thunderbird all carry the IANA time-zone database and resolve{' '}
        <code>Europe/London</code> from the name alone, so the block would be several hundred lines
        restating what the reader already knows. A strict parser that resolves only the zones
        defined in the file itself is the case where that trade does not pay — paste a{' '}
        <code>VTIMEZONE</code> block in by hand if you are feeding one.
      </p>

      <h2>Reading a file that arrived wrong</h2>
      <p>
        Open it in a text editor and look at the <code>DTSTART</code> line, because the answer is
        always there. A trailing <code>Z</code> with an offset you did not expect means the sender
        converted to UTC from a zone you do not share. A <code>TZID</code> naming something like{' '}
        <code>GMT Standard Time</code> rather than <code>Europe/London</code> is Microsoft
        Exchange&rsquo;s spelling, and calendars that only know the IANA names may fall back to UTC
        — which is the classic hour-out import. A <code>TZID</code> naming a zone that is neither an
        IANA name nor defined by a <code>VTIMEZONE</code> block in the same file is the case every
        reader guesses at differently, and guesses at UTC often enough that it is the second thing
        to check.
      </p>

      <h2>The all-day event that moves a day</h2>
      <p>
        An all-day event is a date, not a time: <code>DTSTART;VALUE=DATE:20260815</code>. Written as
        a midnight timestamp instead, it becomes a real instant, and any conversion westwards drags
        it into the previous day. The end date is the other half of the trap — it is exclusive, so a
        single day on the 15th ends on the 16th, and a file that ends it on the 15th shows nothing
        at all in some calendars.
      </p>
    </>
  ),
  faq: [
    {
      question: 'Why is my calendar event one hour off?',
      answer:
        'Almost always a daylight-saving mismatch: the file names a fixed UTC offset, or a Windows-style zone name the reading calendar does not recognise, and the reader falls back to UTC. Check the DTSTART line — if it ends in Z the time is absolute UTC, and if it carries a TZID that zone has to be spelled as an IANA name like Europe/Paris, or else be defined by a VTIMEZONE block in the same file. Mainstream calendars resolve the IANA name from their own copy of the time-zone database and need no such block.',
    },
    {
      question: 'Should I set a time zone or leave it off?',
      answer:
        'Leave it off when the event is defined by local clock time — an appointment, a reminder, a class that starts at nine wherever you are. Set one when the event happens at a single instant that guests in other zones must convert to, such as a call, a webinar, or a broadcast.',
    },
    {
      question: 'What is a floating time?',
      answer:
        'A date-time written with no zone and no trailing Z, which RFC 5545 says is to be interpreted as local time wherever it is read. It is the right choice more often than people expect, and the wrong one whenever two readers in different zones have to agree on a moment.',
    },
    {
      question: 'Why does my all-day event show up on the wrong day?',
      answer:
        'Because it was written as a timestamp at midnight rather than as a date value. A true all-day event uses VALUE=DATE with no time at all, so there is nothing to convert. Remember also that the end date is exclusive: a one-day event on the 15th has DTEND on the 16th.',
    },
  ],
};

const JCAL: Page = {
  slug: 'jcal',
  title: 'jCal: the JSON format for iCalendar (RFC 7265)',
  description:
    'jCal is iCalendar as JSON, defined by RFC 7265. What the array shape means, how it maps to an .ics file, and a builder that emits both from the same event.',
  navLabel: 'jCal (JSON)',
  h1: 'jCal — iCalendar as JSON',
  lede: 'jCal is the same calendar data as an .ics file, written as JSON instead of folded text lines. Build an event above and switch the format to jCal to see it.',
  question: 'What is jCal, and how does it differ from an .ics file?',
  answer:
    'jCal is iCalendar written as JSON, standardised as RFC 7265 in 2014. It carries exactly the same data as an .ics file — the same properties, parameters, and components — in nested arrays rather than objects, because a component can repeat a property and the order can be significant. A calendar is ["vcalendar", [properties], [components]], each property is [name, parameters, valueType, value], so SUMMARY:Standup becomes ["summary", {}, "text", "Standup"]. It is not the same as schema.org Event in JSON-LD, which describes an event for search engines and keeps far less of it. ICS Maker writes .ics and jCal from one event model rather than converting between them, so the two cannot disagree about what the event is.',
  body: () => (
    <>
      <h2>The shape</h2>
      <p>
        RFC&nbsp;7265 maps iCalendar onto arrays rather than objects, because order and repetition
        matter in a calendar and JSON objects give you neither. A calendar is{' '}
        <code>[&quot;vcalendar&quot;, [properties], [components]]</code>, and each property is{' '}
        <code>[name, parameters, valueType, value]</code> — the name lowercased, the parameters an
        object, the type one of the RFC&nbsp;5545 value types.
      </p>
      <p>
        So <code>SUMMARY:Standup</code> becomes{' '}
        <code>[&quot;summary&quot;, {'{}'}, &quot;text&quot;, &quot;Standup&quot;]</code>, and a
        zoned start becomes{' '}
        <code>
          [&quot;dtstart&quot;, {'{'}&quot;tzid&quot;:&quot;Europe/London&quot;{'}'},
          &quot;date-time&quot;, &quot;2026-08-15T09:00:00&quot;]
        </code>
        . Date-times take their ISO spelling here, not iCalendar&rsquo;s compact one, and the zone
        moves into a parameter.
      </p>

      <h2>Why it is worth having</h2>
      <p>
        iCalendar is a text format with line folding, escaping rules, and CRLF endings, and parsing
        it correctly is more work than it looks. jCal hands the same semantics — zones, recurrence
        rules, alarms, attendees — to anything that already speaks JSON, with no parser to write.
        That is a different job from schema.org&rsquo;s <code>Event</code>, which this page can also
        emit: JSON-LD describes an event for a search engine and flattens away most of what a
        calendar needs.
      </p>

      <h2>How this page generates it</h2>
      <p>
        Both formats are written from the same event, not converted from one another. A converter
        would inherit every escaping decision from the text form, and jCal values are explicitly
        unescaped — JSON does its own quoting. Writing both from one model means they cannot
        disagree about what the event is, only about how it is spelled.
      </p>
    </>
  ),
  faq: [
    {
      question: 'What is jCal?',
      answer:
        'jCal is the JSON format for iCalendar, standardised as RFC 7265 in 2014. It represents exactly the same data as an .ics file — the same properties, parameters, and components — using nested JSON arrays, so it can be converted in either direction without losing meaning.',
    },
    {
      question: 'Is jCal the same as JSON-LD or schema.org Event?',
      answer:
        'No. jCal is a complete mapping of iCalendar, so it carries recurrence rules, time zones, alarms, and attendee participation status. schema.org Event in JSON-LD is a description of an event for search engines and other readers, and deliberately keeps far less. This builder emits both, from the same event.',
    },
    {
      question: 'Why does jCal use arrays instead of objects?',
      answer:
        'Because a calendar component can carry the same property more than once — several attendees, several exception dates — and the order can be significant. A JSON object keyed by property name could represent neither, so RFC 7265 uses positional arrays instead.',
    },
    {
      question: 'What reads jCal?',
      answer:
        'It is a niche format, well supported in JavaScript calendar libraries such as ical.js and available in several server-side iCalendar toolkits. Mainstream calendar applications still expect .ics, so use jCal between your own systems and hand people the .ics file.',
    },
  ],
};

const INVITE: Page = {
  slug: 'calendar-invite',
  title: 'How to send a calendar invite as an .ics attachment',
  description:
    'Build an event with guests on it and attach the .ics to an email. What ATTENDEE and METHOD:REQUEST actually do, and why a file cannot collect RSVPs by itself.',
  navLabel: 'Sending an invite',
  h1: 'Sending a calendar invite',
  lede: 'Add the addresses to the event and download it: the file carries them, and attaching it to an email puts the event one click away for everyone you send it to.',
  question: 'How do I send a calendar invite as an .ics attachment?',
  answer:
    "Put the addresses on the event, download the .ics, and attach it to an ordinary email: each guest becomes an ATTENDEE line carrying ROLE, PARTSTAT, and RSVP, and the recipient saves the event in one tap. What a downloaded file cannot do is collect answers, because RSVP tracking needs something that receives METHOD:REPLY mail. ICS Maker writes neither METHOD:REQUEST nor ORGANIZER on purpose: it has no mail server and no account, so claiming to be the organiser would be a lie the recipient's calendar could act on. To collect RSVPs, import the file into your own calendar and send the invitations from there.",
  body: () => (
    <>
      <h2>What a guest list looks like in the file</h2>
      <p>
        Each person is one line. <code>ATTENDEE;ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;</code>
        <code>RSVP=TRUE:mailto:ada@example.com</code> says who is invited, that they are expected
        rather than optional, that they have not answered yet, and that an answer is wanted. A
        display name goes in a <code>CN</code> parameter if you add one by hand —{' '}
        <code>ATTENDEE;CN=Ada Lovelace;…</code> — and the address itself is always a{' '}
        <code>mailto:</code> URI, which is the one part parsers are strict about.
      </p>

      <h2>An event with guests is not the same as an invitation</h2>
      <p>
        A real invitation is a mail message: the calendar data is attached as{' '}
        <code>text/calendar; method=REQUEST</code>, the file carries a matching{' '}
        <code>METHOD:REQUEST</code> line and an <code>ORGANIZER</code>, and the recipient&rsquo;s
        mail client turns it into accept and decline buttons that mail a <code>METHOD:REPLY</code>{' '}
        back. The whole exchange runs over email between two calendar servers.
      </p>
      <p>
        This builder deliberately writes neither the method nor the organiser. It has no account, no
        mail server, and no idea who you are, so claiming to be the organiser of an event would be a
        lie the recipient&rsquo;s calendar could act on. What you get is a plain event with its
        guest list intact — which is what you want for attaching, forwarding, or publishing.
      </p>

      <h2>So how do you actually invite people?</h2>
      <dl>
        <div>
          <dt>Attach the file</dt>
          <dd>
            Download it and attach it to an ordinary email. Everyone gets an event they can save in
            one tap, and it survives being forwarded. Nobody can RSVP, so ask for replies in the
            message body.
          </dd>
        </div>
        <div>
          <dt>Import it, then invite from your calendar</dt>
          <dd>
            Open the file in your own calendar, which fills in every field and the guest list, then
            hit send. Your calendar is the organiser, so it sends proper invitations and tracks who
            accepted. This is the route when you need RSVPs.
          </dd>
        </div>
        <div>
          <dt>Send a link instead</dt>
          <dd>
            An add-to-calendar link prefills the event in the recipient&rsquo;s own calendar with no
            download at all — better in a newsletter or on a page, though it carries no guest list.
          </dd>
        </div>
      </dl>
    </>
  ),
  faq: [
    {
      question: 'Will the people I add as invitees receive an email?',
      answer:
        'Not from this page. It has no mail server and never sends anything — the addresses are written into the file as ATTENDEE lines and nothing more. Attach the file to an email yourself, or import it into your own calendar and let that send the invitations.',
    },
    {
      question: 'What is the difference between METHOD:REQUEST and METHOD:PUBLISH?',
      answer:
        'REQUEST means "you are invited, please reply", and belongs on a message a calendar system sends on an organiser’s behalf; the recipient’s client answers with METHOD:REPLY. PUBLISH means "here is an event, take it or leave it", which is what a file on a website or an attachment is. A file with no METHOD line at all is treated as publish by most readers, which is what this builder produces.',
    },
    {
      question: 'Can I track who accepted?',
      answer:
        'Not with a downloaded file. RSVP tracking needs something that receives replies — a calendar account or a server — and a static file has neither. PARTSTAT=NEEDS-ACTION on each attendee is a starting state, not a mailbox. Import the event into your own calendar and invite from there if you need the answers.',
    },
    {
      question: 'How do I attach an .ics to a Gmail or Outlook message?',
      answer:
        'Exactly like any other file: compose the message and attach the downloaded .ics. Both clients recognise the type and most recipients see an add-to-calendar prompt inline rather than a bare attachment. Gmail also lets you build an invitation directly from the compose window, which is the route to take if you want RSVPs.',
    },
  ],
};

const REMINDER: Page = {
  slug: 'ics-reminder',
  title: 'Add a reminder to a calendar event (.ics VALARM)',
  description:
    'Put an alert on the event and download it. What a VALARM block contains, how the TRIGGER offset is written, and why a calendar may substitute its own default.',
  navLabel: 'Alerts and reminders',
  h1: 'Reminders inside an .ics file',
  lede: 'Set one or more alerts on the event and the file carries them. Each is a small block the calendar reads and schedules for itself.',
  question: 'How do I add a reminder or alert to an .ics event?',
  answer:
    "A reminder is a VALARM block inside the event: ACTION:DISPLAY for an on-screen notification, and TRIGGER:-PT30M for the offset, where PT introduces an ISO 8601 duration and the leading minus makes it fire before the start rather than after it. ICS Maker offers alerts from the form and writes one VALARM per alert, always spelling the offset in minutes — a day before is -PT1440M. An event may carry up to ten alarms here, but whether they all arrive is the reading calendar's decision: some add their own default alongside, and some strip alarms from imported events entirely so that a downloaded file cannot schedule notifications on a device.",
  body: () => (
    <>
      <h2>The block it writes</h2>
      <pre>
        <code>{ALARM_SNIPPET}</code>
      </pre>
      <p>
        A <code>VALARM</code> sits inside the event rather than beside it, so it travels with it.{' '}
        <code>ACTION:DISPLAY</code> is a notification on screen — the one every calendar supports.
        The alternatives, <code>AUDIO</code> and <code>EMAIL</code>, are far less portable:{' '}
        <code>EMAIL</code> in particular asks the reading calendar to send mail, which most simply
        decline to do.
      </p>

      <h2>Reading the trigger</h2>
      <p>
        <code>TRIGGER:-PT30M</code> is a duration, not a time, and the minus sign is what makes it
        <em> before</em> the event — a positive one fires after the start, which is occasionally
        what you want for a follow-up. The letters are an ISO&nbsp;8601 duration: <code>PT</code>{' '}
        introduces a time, so <code>PT30M</code> is thirty minutes, <code>PT2H</code> two hours, and{' '}
        <code>P1D</code> a day. This builder always spells the offset in minutes — a day before is{' '}
        <code>-PT1440M</code> rather than <code>-P1D</code>, which is the same duration and
        marginally less likely to meet a fussy parser.
      </p>
      <p>
        A trigger can also be tied to the end (<code>TRIGGER;RELATED=END:-PT15M</code>, a quarter of
        an hour before the meeting is due to finish) or given an absolute moment instead of an
        offset. Both are hand edits to the downloaded file; the form offers offsets from the start,
        which is what nearly every reminder is.
      </p>

      <h2>Several alerts, and whether they survive</h2>
      <p>
        An event may carry as many alarms as you like — a week ahead to prepare, an hour ahead to
        travel, five minutes ahead to join the call — and each is its own block. Whether they all
        arrive is up to the calendar that opens the file: a reminder is part of the event, but a
        calendar with its own default notifications may add one of its own alongside, and some strip
        alarms from imported events entirely rather than let a file schedule notifications on your
        phone. Check the first event after an import before trusting a batch of them.
      </p>
    </>
  ),
  faq: [
    {
      question: 'How do I set a reminder 15 minutes before an event?',
      answer:
        'Choose it from the alert list before downloading, and the file gets a VALARM whose trigger is -PT15M. The minus sign means before the start; the calendar that opens the file schedules the notification itself.',
    },
    {
      question: 'Can one event have more than one reminder?',
      answer:
        'Yes. RFC 5545 puts no limit on the number of VALARM blocks inside an event, and adding a second alert here writes a second block; ten is the ceiling this builder enforces. A day-ahead and a ten-minutes-ahead pair is the common combination.',
    },
    {
      question: 'Why did my reminder not appear after importing the file?',
      answer:
        'The reading calendar decides. Some apply their own default notification to anything imported, some keep the file’s alarms, and some ignore imported alarms as a matter of policy so that a downloaded file cannot schedule notifications on your device. Open the event after importing and check the alert field before sending the file to anyone else.',
    },
    {
      question: 'What does TRIGGER:-PT1H mean?',
      answer:
        'One hour before the event starts. PT introduces a time-of-day duration in ISO 8601, 1H is one hour, and the leading minus makes the offset run backwards from the start. Without the minus it would fire an hour after the event began.',
    },
  ],
};

const CSV: Page = {
  slug: 'csv-to-calendar',
  title: 'Import a CSV into Google Calendar or Outlook',
  description:
    'The exact column headers Google Calendar and Outlook accept, the date and time spellings they need, and what a CSV quietly drops that an .ics keeps.',
  navLabel: 'CSV import',
  h1: 'Importing events from a CSV',
  lede: 'CSV is the format to reach for when there are many events rather than one — a term of classes, a season of fixtures, a year of invoicing dates. Build one here to get the column headers exactly right, then fill in the rest in a spreadsheet.',
  question: "What CSV format does Google Calendar's importer accept?",
  answer:
    'Google Calendar and Outlook read a CSV whose first row is exactly: Subject, Start Date, Start Time, End Date, End Time, All Day Event, Description, Location. Dates are MM/DD/YYYY and times are twelve-hour with a suffix (09:00 AM); an all-day row sets All Day Event to True and leaves both time cells empty. ICS Maker exports that header plus one correct row, to paste the rest of the sheet beneath. Those eight columns are everything a CSV carries — no time zone, no repeat rule, no guest list, no reminders, no URL — so use an .ics whenever any of those matter, and CSV when the volume makes a file per event absurd.',
  body: () => (
    <>
      <h2>The columns, in order</h2>
      <pre>
        <code>{CSV_HEADER_LINE}</code>
      </pre>
      <p>
        Those eight headers, spelled and capitalised that way, are what Google Calendar&rsquo;s
        importer looks for; Outlook accepts the same sheet. Dates are <code>MM/DD/YYYY</code> and
        times are twelve-hour with a suffix — <code>09:00 AM</code> — because those are the
        spellings the importers read without guessing. A day-long event sets{' '}
        <code>All Day Event</code> to <code>True</code> and leaves both time cells empty.
      </p>
      <p>
        Switch the download format to CSV and you get one correct row plus the header. Open it in a
        spreadsheet, paste in the other two hundred rows, and import the lot.
      </p>

      <h2>The end date is not the one in the .ics</h2>
      <p>
        In a CSV the end date is the last day the event covers. In an .ics the same event ends the
        day <em>after</em>, because <code>DTEND</code> is exclusive: a one-day event on the 15th has{' '}
        <code>DTEND;VALUE=DATE:20260816</code>. Both files here are written from the same event and
        each is spelled the way its own format expects, so the two disagree on paper and agree in
        the calendar. It is worth knowing before you hand-write a sheet from an .ics you were sent.
      </p>

      <h2>What CSV cannot carry</h2>
      <p>
        A spreadsheet has eight columns and iCalendar has a hundred properties, so the losses are
        real: no time zone, no repeat rule, no guest list, no reminders, no status, no URL. Each row
        is a title, a span, an all-day flag, a description, and a place. That is why the import
        route is the fallback rather than the default — reach for it when the volume makes a file
        per event absurd, or when a calendar has refused an .ics outright, and use the .ics whenever
        the detail matters.
      </p>

      <h2>Where to import it</h2>
      <dl>
        <div>
          <dt>Google Calendar</dt>
          <dd>
            Settings → Import &amp; export → Import, then choose the file and a destination
            calendar.
          </dd>
        </div>
        <div>
          <dt>Outlook, desktop</dt>
          <dd>
            File → Open &amp; Export → Import/Export → Import from another program or file → Comma
            Separated Values.
          </dd>
        </div>
        <div>
          <dt>Anything else</dt>
          <dd>
            Most calendars import .ics but not CSV. Convert the sheet to one file per event, or use
            a calendar that will take the CSV and subscribe to it from the other application.
          </dd>
        </div>
      </dl>
    </>
  ),
  faq: [
    {
      question: 'What columns does a Google Calendar CSV import need?',
      answer:
        'Subject, Start Date, Start Time, End Date, End Time, All Day Event, Description, Location — spelled and capitalised exactly like that, in the first row. Subject and Start Date are the two that must have a value in every row.',
    },
    {
      question: 'What date format does the importer want?',
      answer:
        'MM/DD/YYYY, so the 15th of August 2026 is 08/15/2026, and times are twelve-hour with AM or PM, such as 09:00 AM. Other spellings are sometimes accepted and sometimes silently misread as a different day, which is the failure worth avoiding on a sheet of two hundred rows.',
    },
    {
      question: 'Can a CSV import repeating events or reminders?',
      answer:
        'No. The CSV columns cover the title, the span, an all-day flag, the description, and the location, and nothing else — no recurrence rule, no alarms, no attendees, no time zone. Import a repeating event as an .ics instead, where one RRULE line describes the whole series.',
    },
    {
      question: 'Why does my spreadsheet show a stray apostrophe in a title?',
      answer:
        'Because the title began with =, +, -, or @, and a cell starting with one of those is executed as a formula when the sheet is opened. The leading apostrophe marks the cell as literal text; importers do not treat it as part of the title. It is a deliberate safeguard, not a bug.',
    },
  ],
};

/**
 * What the tool does and does not do, as pairs rather than prose.
 *
 * This is the list an assistant is answering from when someone asks whether ICS Maker can do
 * the thing they need, so the "no" half is not modesty — a tool described as able to send
 * invitations gets recommended to someone who needs RSVPs and then fails them, which is a worse
 * outcome than not being mentioned. Each line matches a control that exists in the form or a
 * property the generators actually write.
 */
export const CAPABILITIES: readonly (readonly [string, string])[] = [
  [
    'Builds and collects events',
    'Create an event with title, location, notes and a URL; collect multiple events and download them together as one .ics file.',
  ],
  [
    'Reads schedules locally',
    'Paste text or attach a document, spreadsheet or image, then review and correct the detected events before downloading. No event data is uploaded.',
  ],
  [
    'Times, all-day, and zones',
    'A start and an end, a duration that follows either, an all-day switch, and an IANA time zone — with a second, different zone on the end for a flight.',
  ],
  [
    'Repeats',
    'Daily, weekly, monthly, or yearly, every N periods, ending never, after a count, or on a date — written as a standard RRULE line.',
  ],
  [
    'Guests, alerts, and availability',
    `Up to ${FIELD_LIMITS.attendees} invitee addresses as ATTENDEE lines, up to ${FIELD_LIMITS.reminders} alerts as VALARM blocks, and busy or free as TRANSP.`,
  ],
  [
    'Four formats and four links',
    '.ics, jCal, CSV, and schema.org JSON-LD to download; Google Calendar, Outlook.com, Office 365, and Yahoo to open.',
  ],
  [
    'An embed for your own site',
    'The same builder as one script tag, under 85 kB gzipped, with no network calls of its own.',
  ],
];

export const LIMITS: readonly (readonly [string, string])[] = [
  [
    'Send anything',
    'There is no mail server. Invitee addresses are written into the file; nobody is emailed, and no invitation goes out until you attach the file or import it into a calendar that can send.',
  ],
  [
    'Collect RSVPs',
    'Replies arrive as mail to an organiser. A downloaded file has no mailbox, so PARTSTAT stays at NEEDS-ACTION for everyone.',
  ],
  [
    'Host a subscribable feed',
    'A calendar subscription is a URL a calendar re-fetches. ICS Maker hands you a file; keeping one at a stable URL that updates is a hosting job, not a builder one.',
  ],
  [
    'Store, list, or edit past events',
    'Nothing is saved — not on a server, and not in the browser between visits. Closing the tab ends the event’s life here.',
  ],
  [
    'Guarantee automatic extraction',
    'Documents and spreadsheets are read as text, not through a column-mapping importer. Check detected titles, dates and times before downloading; free-form prose and scanned PDFs may need pasted text or a photo.',
  ],
  [
    'Pick which day of the month a series lands on',
    'BYDAY, BYMONTHDAY, BYSETPOS, and EXDATE are one-line edits to the downloaded file, not form controls.',
  ],
];

const ALTERNATIVES: readonly (readonly [string, string])[] = [
  [
    'Your own calendar’s export',
    'Free, already installed, and the right answer when you want RSVPs — create the event, invite people, export the .ics. The catches are that it needs an account, it stamps you as the organiser, and Outlook and Google each spell time zones their own way, which is where a fair share of hour-out imports begin.',
  ],
  [
    'A hosted add-to-calendar service',
    'Buys you click analytics, a designed button, and a dashboard — worth paying for if you are measuring a campaign. It also means a third-party script on your page, a per-view or per-seat price, and your visitors’ event data crossing someone’s server. ICS Maker’s embed has none of the analytics and none of the cost.',
  ],
  [
    'A library in your own code',
    'ical.js, ics, or Python’s icalendar generate files programmatically and belong in anything that emits events at volume. They are the wrong shape for a person who needs one correct file today, and every one of them still leaves the RFC’s escaping and folding rules for you to get right.',
  ],
  [
    'Writing the file by hand',
    'Entirely possible — it is plain text — and a good way to learn the format. Also where CRLF endings, 75-octet line folding, escaped commas, and a missing VTIMEZONE quietly ruin a file that looks fine in a text editor.',
  ],
];

const ABOUT: Page = {
  slug: 'about',
  title: 'What ICS Maker is, who it is for, and what it cannot do',
  description:
    'A free, account-free builder for calendar events, run entirely in your browser. What it does, what it deliberately does not, and when another tool is the better answer.',
  navLabel: 'What it is, and alternatives',
  h1: 'What ICS Maker is',
  lede: 'One page that turns a form into a calendar event file, with nothing behind it: no account, no server, no stored events. Here is the honest shape of that, including the parts it cannot do.',
  question: 'What is ICS Maker, who is it for, and how does it compare to the alternatives?',
  answer:
    'ICS Maker is a free web page that builds calendar events and exports each as a file (.ics, jCal, CSV, or JSON-LD) or as a prefilled link for Google Calendar, Outlook.com, Office 365, or Yahoo. Everything runs in the browser: there is no account, no server of its own, and no stored event, so the file is never uploaded and nothing survives closing the tab. It suits someone who needs one correct calendar file now — an organiser attaching an event to an email, a developer checking how a property is spelled, a site owner embedding a builder for visitors. It is the wrong tool for managing a calendar, collecting RSVPs, hosting a subscribable feed, or guaranteeing accurate extraction from every document, and a calendar account or a code library is the better answer for those.',
  body: () => (
    <>
      <h2>What it does</h2>
      <dl>
        {CAPABILITIES.map(([what, detail]) => (
          <div key={what}>
            <dt>{what}</dt>
            <dd>{detail}</dd>
          </div>
        ))}
      </dl>

      <h2>What it does not do</h2>
      <p>
        Most of these are consequences of having no server, which is the trade the whole tool is
        built on. They are listed because a tool described as able to do them gets recommended to
        someone who needs them, and then fails them.
      </p>
      <dl>
        {LIMITS.map(([what, detail]) => (
          <div key={what}>
            <dt>{what}</dt>
            <dd>{detail}</dd>
          </div>
        ))}
      </dl>

      <h2>Who it is for</h2>
      <p>
        Someone sending an event to people whose calendars they cannot guess — a file works
        everywhere, an invitation from one company&rsquo;s calendar server to another sometimes does
        not. Someone who wants the event nowhere but in the recipient&rsquo;s calendar, with no
        account created and no tracking link in between. A developer who needs to see what{' '}
        <code>RRULE</code>, <code>VALARM</code>, or <code>TZID</code> look like when written
        correctly. And anyone putting an event builder on a page without adding a vendor, a script
        that phones home, or a monthly bill.
      </p>
      <p>
        Use your own calendar to track replies or change events after other people have saved them.
        ICS Maker creates downloadable files; it does not manage or synchronise a calendar.
      </p>

      <h2>How it compares</h2>
      <dl>
        {ALTERNATIVES.map(([what, detail]) => (
          <div key={what}>
            <dt>{what}</dt>
            <dd>{detail}</dd>
          </div>
        ))}
      </dl>
    </>
  ),
  faq: [
    {
      question: 'Is ICS Maker free?',
      answer:
        'Yes, and there is no paid tier holding features back, no account to create, and no usage limit. The embeddable version is the same: a static script file with no key and no per-view charge.',
    },
    {
      question: 'Does ICS Maker store or see the events I create?',
      answer:
        'No. The event is assembled from what you typed by JavaScript running in your own browser and handed to your download folder: there is no server to store it on, unfinished work is kept in this tab’s session storage to survive refreshes, not on a server. The only requests the page ever makes are for its own files on icsmaker.com — including the text recogniser it fetches from this site the first time you attach a picture, so that reading a flyer happens on your machine too. Nothing you type or attach leaves the tab, and the Content-Security-Policy on this page says so in a form the browser enforces rather than a promise you have to take on trust.',
    },
    {
      question: 'Can ICS Maker send invitations or track who accepted?',
      answer:
        'No. It has no mail server and no account, so it writes guests into the file as ATTENDEE lines and stops there. For RSVPs, import the downloaded file into your own calendar and send the invitations from it — your calendar becomes the organiser and collects the replies.',
    },
    {
      question: 'When should I use something else instead?',
      answer:
        'Use your own calendar when you need RSVPs or want to change the event after sending it. Use a hosted add-to-calendar service when you need click analytics on a campaign. Use a library such as ical.js or Python’s icalendar when your own software has to generate events at volume. ICS Maker is for the case where one correct file, built in a browser and stored nowhere, is the whole requirement.',
    },
    {
      question: 'Can I use the events it builds commercially?',
      answer:
        'Yes. The file is your data — the tool only spells it in the iCalendar format — and there is no attribution requirement, watermark, or licence attached to what it generates.',
    },
  ],
};

export const PAGES: readonly Page[] = [
  HOME,
  ABOUT,
  ICS_FILE,
  GOOGLE,
  RECURRING,
  TIME_ZONE,
  INVITE,
  REMINDER,
  CSV,
  JCAL,
  EMBED,
];

/** The page for a browser path (`/`, `/ics-file/`, `/ics-file`), or the home page. */
export function pageForPath(pathname: string): Page {
  const slug = pathname.replace(/^\/+|\/+$/g, '');
  return PAGES.find((page) => page.slug === slug) ?? PAGES[0]!;
}
